// The plan sheet as sharp as Fieldwire shows it (Ryan, Oct 5 2026: "in Fieldwire it seems
// sharper"). Fieldwire's file_url picture is a shrunk copy — ~2450 px wide for a sheet
// whose original is 5400 px — so the viewers show that copy at once, then draw the
// sheet's ORIGINAL PDF page with pdf.js at full size and swap it in.
//
// Safe by design: if there is no PDF, it fails to load, or the page doesn't have the
// same shape as the sheet (a rotated upload — pins would land wrong), the shrunk copy
// simply stays. Capped by pixels so a phone isn't asked for more than it can hold.
import { useEffect, useState } from 'react';
import { API_BASE, getToken } from '../../services/apiClient';

const cache = new Map();    // floorplan id → blob URL (last few, newest last)
const KEEP = 4;
const remember = (id, url) => {
  cache.set(id, url);
  while (cache.size > KEEP) { const [k, u] = cache.entries().next().value; cache.delete(k); try { URL.revokeObjectURL(u); } catch { /* gone */ } }
};
const inflight = new Map(); // floorplan id → Promise<url|null>

const maxPixels = () => {
  try { if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return 10e6; } catch { /* old browser */ }
  return 16e6;
};

async function drawSharp(projectId, sheet) {
  const r = await fetch(`${API_BASE}/projects/${projectId}/fieldwire/sheets/${sheet.id}/original`, { headers: { 'x-worker-token': getToken() } });
  if (!r.ok) return null;
  const data = new Uint8Array(await r.arrayBuffer());
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = (process.env.PUBLIC_URL || '') + '/pdf.worker.min.js';
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  try {
    const pn = Number(sheet.page_number) || 1;
    const page = await pdf.getPage(pn >= 1 && pn <= pdf.numPages ? pn : 1);
    const want = sheet.file_width && sheet.file_height ? sheet.file_width / sheet.file_height : null;
    // The page as it reads, or turned a quarter, whichever has the sheet's shape.
    let rotation = page.rotate || 0, base = page.getViewport({ scale: 1, rotation });
    if (want) {
      const fits = v => Math.abs(v.width / v.height - want) / want < 0.03;
      if (!fits(base)) {
        const turned = page.getViewport({ scale: 1, rotation: (rotation + 90) % 360 });
        if (!fits(turned)) return null;
        rotation = (rotation + 90) % 360; base = turned;
      }
    }
    const targetW = Math.max(sheet.original_width || 0, (sheet.file_width || 0) * 2, 3000);
    let scale = targetW / base.width;
    const cap = Math.sqrt(maxPixels() / (base.width * base.height));
    scale = Math.min(scale, cap);
    if (sheet.file_width && base.width * scale < sheet.file_width * 1.15) return null;   // not worth the swap
    const vp = page.getViewport({ scale, rotation });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(vp.width); canvas.height = Math.floor(vp.height);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
    canvas.width = canvas.height = 0;   // hand the memory back now
    return blob ? URL.createObjectURL(blob) : null;
  } finally {
    try { pdf.destroy(); } catch { /* fine */ }
  }
}

// → { src, note }: src is the sharp copy once it is drawn, else the sheet's own picture.
export default function useSharpSheet(projectId, sheet) {
  const id = sheet && sheet.id;
  const [sharp, setSharp] = useState(() => (id && cache.get(id)) || null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!projectId || !id || !sheet.has_pdf) { setSharp(null); setBusy(false); return undefined; }
    if (cache.has(id)) { setSharp(cache.get(id)); setBusy(false); return undefined; }
    let live = true;
    setSharp(null); setBusy(true);
    let p = inflight.get(id);
    if (!p) {
      p = drawSharp(projectId, sheet).catch(() => null).then(url => { inflight.delete(id); if (url) remember(id, url); return url; });
      inflight.set(id, p);
    }
    p.then(url => { if (live) { setSharp(url); setBusy(false); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, id, sheet && sheet.has_pdf]);
  return { src: sharp || (sheet && sheet.file_url) || null, note: busy ? 'Sharpening the drawing…' : null };
}
